/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Spin } from 'antd'
import colors from '@sportninja/common/constants/appColors'

import { font, weightSemiBold } from 'src/components/css'
import type { CalendarGridProps } from './types'
import CalendarDayCell from './CalendarDayCell'
import { buildMonthDays, todayCivilDate } from './buildCalendarDays'
import { useAnchorDate, useRangeChangeNotifier } from './useAnchorDate'
import { calendarLabel, DAY_HEADER_KEYS } from './calendarI18n'

const CalendarGrid = ({
  items,
  isSoccer,
  isHockey,
  loading,
  onMonthChange,
  anchorDate,
  onAnchorDateChange,
}: CalendarGridProps) => {
  'use memo'

  const { anchor, setValue } = useAnchorDate(anchorDate, onAnchorDateChange)

  const monthStart = anchor.startOf('month')
  const monthEndExclusive = monthStart.plus({ months: 1 })

  useRangeChangeNotifier(
    monthStart.toFormat('yyyy-MM-dd'),
    monthEndExclusive.toFormat('yyyy-MM-dd'),
    onMonthChange
  )

  const goToMonth = (offset: number) =>
    setValue(monthStart.plus({ months: offset }).toFormat('yyyy-MM-dd'))

  const calendarDays = buildMonthDays(anchor, items)

  return (
    <div css={containerStyles} data-calendar-grid>
      <div css={navStyles}>
        <button
          type='button'
          css={navBtnStyles}
          onClick={() => goToMonth(-1)}
          aria-label={calendarLabel('calendarPrevMonth')}
        >
          &#8249;
        </button>
        <span css={monthTitleStyles}>{monthStart.toFormat('LLLL yyyy')}</span>
        <button
          type='button'
          css={navBtnStyles}
          onClick={() => goToMonth(1)}
          aria-label={calendarLabel('calendarNextMonth')}
        >
          &#8250;
        </button>
        <button
          type='button'
          css={todayBtnStyles}
          onClick={() => setValue(todayCivilDate())}
          aria-label={calendarLabel('calendarGoToToday')}
        >
          {calendarLabel('calendarToday')}
        </button>
      </div>

      {loading ? (
        <div css={loadingOverlayStyles}>
          <Spin size='small' />
        </div>
      ) : (
        //  The month grid stays rendered even with no games. Replacing it with a
        //  single "no games" line loses the one thing a calendar is for — seeing
        //  the shape of the month you are looking at.
        <>
          <div css={dayHeaderRowStyles}>
            {DAY_HEADER_KEYS.map((key) => (
              <div key={key} css={dayHeaderCellStyles}>
                {calendarLabel(key)}
              </div>
            ))}
          </div>

          <div css={gridStyles}>
            {calendarDays.map((day) => (
              <CalendarDayCell
                key={day.dayKey}
                day={day}
                isSoccer={isSoccer}
                isHockey={isHockey}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const containerStyles = css`
  background: ${colors.SECONDARY_500};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const navStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 14px 16px;
  background: ${colors.SECONDARY_300};
`

const navBtnStyles = css`
  ${font.body}
  background: transparent;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 6px;
  color: ${colors.NEUTRAL_50};
  cursor: pointer;
  padding: 4px 10px;
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: ${colors.SECONDARY_200};
    color: ${colors.NEUTRAL_0};
  }
  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 1px;
  }
`

const todayBtnStyles = css`
  ${navBtnStyles};
  font-size: 13px;
  padding: 4px 12px;
  margin-left: 8px;
`

//  Body font, not `font.title`. The title face is a condensed all-caps display
//  type (Rift on SportNinja, Neue Plak Condensed on Canlan) — at nav-label size
//  "DECEMBER 2026" is cramped and hard to scan. This is a control label people
//  read at a glance, not a brand moment, and every other surface in the
//  calendar — including the agenda day headers right beneath it — is body font.
const monthTitleStyles = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 18px;
  color: ${colors.NEUTRAL_0};
  min-width: 180px;
  text-align: center;
`

const dayHeaderRowStyles = css`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: ${colors.SECONDARY_400};
  border-bottom: 1px solid ${colors.SECONDARY_200};
`

const dayHeaderCellStyles = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 13px;
  color: ${colors.NEUTRAL_50};
  text-align: center;
  padding: 10px 0;
  letter-spacing: 0.5px;
`

const gridStyles = css`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: ${colors.SECONDARY_500};
`

const loadingOverlayStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  background: ${colors.SECONDARY_500};
`

export default CalendarGrid
